import { useState, useRef } from "react";
import { MediaRecorder, register } from 'extendable-media-recorder';
import { connect } from 'extendable-media-recorder-wav-encoder';
import axios from "axios";
import uuid from 'react-native-uuid';
import { useDispatch, useSelector } from 'react-redux'
import { setBnText, setEnText, setEntity, setResult, setSession, resetSession, setChatReply, setChatReplyEn, setCost } from './asrSlice'
import Button from 'react-bootstrap/Button';
import { hideLoader, showLoader } from '../loader/loaderSlice';

const languageModels = {
    // English variants
    'en-US': ['default', 'latest_long', 'latest_short'],
    'en-GB': ['default', 'latest_long', 'latest_short'],
    'en-AU': ['default', 'latest_long', 'latest_short'],
    'en-IN': ['default', 'latest_long', 'latest_short'],
    'en-CA': ['default'],
    'en-IE': ['default'],
    'en-NZ': ['default'],
    'en-PH': ['default'],
    'en-SG': ['default'],
    'en-ZA': ['default'],
    
    // Spanish variants
    'es-ES': ['default', 'latest_long', 'latest_short'],
    'es-US': ['default', 'latest_long', 'latest_short'],
    'es-AR': ['default'],
    'es-BO': ['default'],
    'es-CL': ['default'],
    'es-CO': ['default'],
    'es-CR': ['default'],
    'es-DO': ['default'],
    'es-EC': ['default'],
    'es-SV': ['default'],
    'es-GT': ['default'],
    'es-HN': ['default'],
    'es-MX': ['default'],
    'es-NI': ['default'],
    'es-PA': ['default'],
    'es-PY': ['default'],
    'es-PE': ['default'],
    'es-PR': ['default'],
    'es-UY': ['default'],
    'es-VE': ['default'],

    // French variants
    'fr-FR': ['default', 'latest_long', 'latest_short'],
    'fr-CA': ['default', 'latest_long', 'latest_short'],
    'fr-BE': ['default'],
    'fr-CH': ['default'],

    // Portuguese variants
    'pt-BR': ['default', 'latest_long', 'latest_short'],
    'pt-PT': ['default', 'latest_long', 'latest_short'],

    // Bengali variants
    'bn-BD': ['default', 'latest_long', 'latest_short'],
    'bn-IN': ['default']
};

const languageNames = {
    // English
    'en-US': 'English (United States)',
    'en-GB': 'English (United Kingdom)',
    'en-AU': 'English (Australia)',
    'en-IN': 'English (India)',
    'en-CA': 'English (Canada)',
    'en-IE': 'English (Ireland)',
    'en-NZ': 'English (New Zealand)',
    'en-PH': 'English (Philippines)',
    'en-SG': 'English (Singapore)',
    'en-ZA': 'English (South Africa)',
    
    // Spanish
    'es-ES': 'Spanish (Spain)',
    'es-US': 'Spanish (United States)',
    'es-AR': 'Spanish (Argentina)',
    'es-BO': 'Spanish (Bolivia)',
    'es-CL': 'Spanish (Chile)',
    'es-CO': 'Spanish (Colombia)',
    'es-CR': 'Spanish (Costa Rica)',
    'es-DO': 'Spanish (Dominican Republic)',
    'es-EC': 'Spanish (Ecuador)',
    'es-SV': 'Spanish (El Salvador)',
    'es-GT': 'Spanish (Guatemala)',
    'es-HN': 'Spanish (Honduras)',
    'es-MX': 'Spanish (Mexico)',
    'es-NI': 'Spanish (Nicaragua)',
    'es-PA': 'Spanish (Panama)',
    'es-PY': 'Spanish (Paraguay)',
    'es-PE': 'Spanish (Peru)',
    'es-PR': 'Spanish (Puerto Rico)',
    'es-UY': 'Spanish (Uruguay)',
    'es-VE': 'Spanish (Venezuela)',

    // French
    'fr-FR': 'French (France)',
    'fr-CA': 'French (Canada)',
    'fr-BE': 'French (Belgium)',
    'fr-CH': 'French (Switzerland)',

    // Portuguese
    'pt-BR': 'Portuguese (Brazil)',
    'pt-PT': 'Portuguese (Portugal)',

    // Bengali
    'bn-BD': 'Bengali (Bangladesh)',
    'bn-IN': 'Bengali (India)'
};

const AudioRecorderSTTSA = () => {
    const dispatch = useDispatch()
    const [permission, setPermission] = useState(false);
    const mediaRecorder = useRef(null);
    const [recordingStatus, setRecordingStatus] = useState("inactive");
    const [stream, setStream] = useState(null);
    const [audioChunks, setAudioChunks] = useState([]);
    const [audio, setAudio] = useState(null);
    const [selectedLanguage, setSelectedLanguage] = useState("en-US");
    const [selectedModel, setSelectedModel] = useState("latest_short");

    const chatId = useSelector((state) => state.chat.chatId);

    const appToken = useSelector((state) => state.auth.appToken);

    const bnText = useSelector((state) => state.asr.bnText);

    const getMicrophonePermission = async () => {
        if ("MediaRecorder" in window) {
            try {
                await register(await connect());
                const streamData = await navigator.mediaDevices.getUserMedia({
                    audio: true,
                    video: false,
                });
                setPermission(true);
                setStream(streamData);
                console.log("Permission is provided");
            } catch (err) {
                alert(err.message);
            }
        } else {
            alert("The MediaRecorder API is not supported in your browser.");
        }
    };

    const startRecording = async () => {
        dispatch(resetSession());
        setRecordingStatus("recording");
        const media = new MediaRecorder(stream, { mimeType: 'audio/wav' });
        //set the MediaRecorder instance to the mediaRecorder ref
        mediaRecorder.current = media;
        mediaRecorder.current.start();
        let localAudioChunks = [];
        mediaRecorder.current.ondataavailable = (event) => {
            if (typeof event.data === "undefined") return;
            if (event.data.size === 0) return;
            localAudioChunks.push(event.data);
        };
        setAudioChunks(localAudioChunks);
    };

    const stopRecording = async () => {
        setRecordingStatus("inactive");
        mediaRecorder.current.stop();
        mediaRecorder.current.onstop = async () => {
            const audioBlob = new Blob(audioChunks, { type: audioChunks[0].type });
            const audioUrl = window.URL.createObjectURL(audioBlob);
            // fileName should be the session id
            const fileNameText = uuid.v4();
            const fileName = fileNameText + ".wav";
            const audioFile = new File([audioBlob], fileName);

            setAudio(audioUrl);
            setAudioChunks([]);

            uploadAudio(audioFile, fileNameText);

        };
    };

    const uploadAudio = async (audioFile, fileName) => {
        const formData = new FormData();
        formData.append("file", audioFile); 
        formData.append("language", selectedLanguage);
        formData.append("model", selectedModel);
        formData.append("chatId", chatId);     
        dispatch(showLoader());   

        axios
            .post(`${process.env.REACT_APP_BACKEND_URL}/api/asr-test-sa`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    "X-API-KEY": appToken
                },
            })
            .then((response) => {
                // Get language name and format model name
                const languageName = languageNames[selectedLanguage];
                const modelName = selectedModel.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                
                // Format the text in the requested style
                const formattedText = `${languageName} [${modelName}]:\t${response.data.asrText}`;
                
                // Append new text to existing text with line breaks
                const newText = bnText 
                    ? `${bnText}\n\n${formattedText}` 
                    : formattedText;
                
                dispatch(setBnText(newText));
                dispatch(hideLoader()); 
                dispatch(setSession(fileName));  
            })
            .catch((error) => {
                console.log(error);
                dispatch(hideLoader());
                dispatch(resetSession()); 
            });
    };

    return (
        <div className='recorder mini-card'>
            <div className="d-flex align-items-center mb-3">
                <h4 className="mb-0 me-2">Audio Recorder</h4>
                <p className="mb-0">(audio should be less than 45 seconds)</p>
            </div>
            <main>
                <div className="d-flex align-items-center">
                    {/* Recording controls - 50% width */}
                    <div className="audio-controls me-3" style={{ width: '50%' }}>
                        {!permission ? (                        
                            <Button
                                variant="outline-dark" size="sm" onClick={getMicrophonePermission}>Get Microphone</Button>
                        ) : null}
                        {permission && recordingStatus === "inactive" ? (
                            <Button
                                variant="outline-dark" size="sm" onClick={startRecording}>Start Recording</Button>
                        ) : null}
                        {recordingStatus === "recording" ? (
                            <Button
                                variant="outline-dark" size="sm" onClick={stopRecording}>Stop Recording</Button>
                        ) : null}
                    </div>

                    {/* Language dropdown - 25% width */}
                    <div className="me-4" style={{ width: '25%' }}>
                        <div className="d-flex align-items-center">
                            <label className="form-label me-2 mb-0" style={{ minWidth: '70px' }}>Language</label>
                            <select 
                                value={selectedLanguage} 
                                onChange={(e) => {
                                    setSelectedLanguage(e.target.value);
                                    if (!languageModels[e.target.value].includes(selectedModel)) {
                                        setSelectedModel(languageModels[e.target.value][0]);
                                    }
                                }}
                                className="form-select form-select-sm"
                            >
                                {Object.entries(languageNames).map(([code, name]) => (
                                    <option key={code} value={code}>{name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Model dropdown - 25% width */}
                    <div style={{ width: '25%' }}>
                        <div className="d-flex align-items-center">
                            <label className="form-label me-1 mb-0">Model</label>
                            <select 
                                value={selectedModel} 
                                onChange={(e) => setSelectedModel(e.target.value)}
                                className="form-select form-select-sm"
                            >
                                {languageModels[selectedLanguage].map(model => (
                                    <option key={model} value={model}>
                                        {model.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Audio player section */}
                {audio ? (
                    <div className="audio-container">
                        <br />
                        <audio src={audio} controls></audio>                        
                    </div>
                ) : null}
            </main>
        </div>
    );
};


export default AudioRecorderSTTSA;